import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import { useSelector } from 'react-redux';
import orderService from '../../../../services/orderService'; 
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
 
export const OrderStats = ({ dateRangeTotalOrderCount, dateRangeTotalRevenue, dateRange: externalDateRange }) => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState({});
  const authUserId = useSelector(state => state?.auth?.user?.id);

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    };
  });

  const formatCurrency = (value) => {
    const formattedValue = value?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) || '-';
    return formattedValue.replace('₺', '') + ' ₺';
  };

  const getReports = async () => {
    try {
      setLoading(true);
      const reportsData = {};

      for (let type = 13; type <= 14; type++) {
        const request = {
          userId: authUserId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          type
        };

        const response = await orderService.fetchOrdersReports(request);
        if (response.status && response.data) {
          switch (type) {
            case 13:
              reportsData.orderCounts = response.data;
              break;
            case 14:
              reportsData.orderRevenues = response.data;
              break; 
          }
        }
      }

      setReports(reportsData);
    } catch (error) {
      console.error("Raporlar alınırken hata oluştu:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReports();
  }, [dateRange]);

 
  const calculateStats = () => {
    return {
      dailyStats: {
        orderCount: (reports.orderCounts && reports.orderCounts[0]) ? reports.orderCounts[0].TodayOrders : 0,
        totalRevenue: (reports.orderRevenues && reports.orderRevenues[0]) ? reports.orderRevenues[0].TodayRevenue : 0
      },
      monthlyStats: {
        // Eğer dateRangeTotalOrderCount ve dateRangeTotalRevenue prop'ları geliyorsa (OrderReports'tan), onları kullan
        // Yoksa normal ThisMonthOrders ve ThisMonthRevenue kullan
        orderCount: dateRangeTotalOrderCount !== undefined 
          ? dateRangeTotalOrderCount 
          : ((reports.orderCounts && reports.orderCounts[0]) ? reports.orderCounts[0].ThisMonthOrders : 0),
        totalRevenue: dateRangeTotalRevenue !== undefined 
          ? dateRangeTotalRevenue 
          : ((reports.orderRevenues && reports.orderRevenues[0]) ? reports.orderRevenues[0].ThisMonthRevenue : 0)
      }
    };
  };

  const stats = calculateStats();

  // Tarih aralığı formatını düzenle
  const formatDateRange = () => {
    if (!externalDateRange?.startDate || !externalDateRange?.endDate) {
      return "Bu Ayki";
    }
    
    const start = new Date(externalDateRange.startDate);
    const end = new Date(externalDateRange.endDate);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const dateRangeText = formatDateRange();

  const statistics = [
    {
      title: "Bugün Sipariş Sayısı",
      value: stats.dailyStats.orderCount,
      icon: <ShoppingCartIcon />,
      color: '#4361ee'
    },
    {
      title: "Bugün Toplam Ciro",
      value: formatCurrency(stats.dailyStats.totalRevenue),
      icon: <AccountBalanceWalletIcon />,
      color: '#3bc9db'
    },
    {
      title: externalDateRange ? `${dateRangeText} Sipariş Sayısı` : "Bu Ayki Sipariş Sayısı",
      value: stats.monthlyStats.orderCount,
      icon: <ShoppingCartIcon />,
      color: '#4361ee'
    },
    {
      title: externalDateRange ? `${dateRangeText} Toplam Ciro` : "Bu Ayki Toplam Ciro",
      value: formatCurrency(stats.monthlyStats.totalRevenue),
      icon: <AccountBalanceWalletIcon />,
      color: '#3bc9db'
    }
  ];

  const StatCard = ({ title, value, icon, color }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 4,
        background: 'white',
        border: '1px solid rgba(0,0,0,0.07)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
          '& .stat-icon-container': {
            transform: 'translateY(0) scale(1)',
            opacity: 1,
          },
          '& .stat-value': {
            color,
            transform: 'translateY(0)',
          }
        }
      }}
    >
      {/* Title at the top */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          mt: 1
        }}
      >
        {title}
      </Typography>

      {/* Value with animated transition */}
      <Typography
        className="stat-value"
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'text.primary',
          fontSize: { xs: '1.75rem', sm: '2.2rem' },
          transition: 'all 0.3s ease',
          transform: 'translateY(4px)',
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Typography>

      {/* Hidden icon that appears on hover */}
      <Box
        className="stat-icon-container"
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 'auto',
          pt: 1,
          transform: 'translateY(10px) scale(0.9)',
          opacity: 0.6,
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
            mr: 1.5,
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: 20,
              color: color,
              mr: 1,
            }
          })}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            Güncel Veri
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
 
  return (
    <Box >
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statistics.map((stat, index) => (
          <Grid item xs={12} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
